import { Link } from 'react-router-dom';
import { serviceAreaCities } from '../../../mocks/serviceAreaCities';
import { useState, useRef, useEffect } from 'react';

export default function ServiceAreasList() {
  const [selectedRegion, setSelectedRegion] = useState<string>('all');
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setIsVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  // Group cities by region
  const regions = {
    'oregon-coast': {
      name: 'Oregon Coast',
      cities: serviceAreaCities.filter(city => 
        ['warrenton', 'astoria', 'seaside', 'cannon-beach', 'tillamook', 'lincoln-city', 'newport', 'florence', 'coos-bay'].includes(city.slug)
      )
    },
    'portland-metro': {
      name: 'Portland Metro',
      cities: serviceAreaCities.filter(city => 
        ['portland', 'hillsboro', 'forest-grove', 'beaverton', 'tigard', 'lake-oswego', 'oregon-city', 'gresham', 'milwaukie'].includes(city.slug)
      )
    },
    'willamette-valley': {
      name: 'Willamette Valley',
      cities: serviceAreaCities.filter(city => 
        ['salem', 'albany', 'corvallis', 'eugene', 'springfield'].includes(city.slug)
      )
    },
    'southwest-wa': {
      name: 'Southwest Washington',
      cities: serviceAreaCities.filter(city => 
        ['longview', 'kelso', 'vancouver', 'camas', 'washougal', 'battle-ground', 'ridgefield'].includes(city.slug)
      )
    },
    'olympia-region': {
      name: 'Olympia Region',
      cities: serviceAreaCities.filter(city => 
        ['olympia', 'lacey', 'tumwater', 'centralia', 'chehalis'].includes(city.slug)
      )
    },
    'washington-coast': {
      name: 'Washington Coast',
      cities: serviceAreaCities.filter(city => 
        ['long-beach', 'ilwaco', 'ocean-park', 'westport', 'aberdeen', 'hoquiam', 'raymond', 'south-bend', 'cathlamet'].includes(city.slug)
      )
    },
    'major-metros': {
      name: 'Major Metro Areas',
      cities: serviceAreaCities.filter(city => 
        ['seattle', 'tacoma', 'spokane', 'bend'].includes(city.slug)
      )
    }
  };

  const displayedCities = selectedRegion === 'all'
    ? serviceAreaCities
    : regions[selectedRegion as keyof typeof regions]?.cities || [];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          ref={ref}
          className={`text-center mb-12 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <h2 className="font-['Oswald'] text-4xl md:text-5xl font-bold text-[#1C2B1E] mb-4">
            Cities We Serve
          </h2>
          <div className="w-24 h-1 bg-[#E8B84B] mx-auto mb-6"></div>
          <p className="text-lg text-[#374151] max-w-2xl mx-auto">
            Browse by region or view all service areas. Click any city to learn more.
          </p>
        </div>

        {/* Region Filter Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          <button
            onClick={() => setSelectedRegion('all')}
            className={`px-6 py-3 rounded-lg font-semibold transition-all whitespace-nowrap ${
              selectedRegion === 'all'
                ? 'bg-[#E8B84B] text-[#1A2332] shadow-md'
                : 'bg-[#F7F5F0] text-[#1A2332] border border-[#E8E2D6] hover:border-[#E8B84B]'
            }`}
          >
            All Areas
          </button>
          {Object.entries(regions).map(([key, region]) => (
            <button
              key={key}
              onClick={() => setSelectedRegion(key)}
              className={`px-6 py-3 rounded-lg font-semibold transition-all whitespace-nowrap ${
                selectedRegion === key
                  ? 'bg-[#E8B84B] text-[#1A2332] shadow-md'
                  : 'bg-[#F7F5F0] text-[#1A2332] border border-[#E8E2D6] hover:border-[#E8B84B]'
              }`}
            >
              {region.name}
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayedCities.map((city, index) => (
            <Link
              key={city.slug}
              to={`/service-areas/${city.slug}`}
              className={`bg-[#F7F5F0] rounded-xl p-6 border border-[#E8E2D6] hover:shadow-lg transition-all duration-300 hover:-translate-y-1 group ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 50}ms` }}
            >
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-['Oswald'] text-xl font-bold text-[#1C2B1E] mb-1">
                    {city.name}
                  </h3>
                  <p className="text-sm text-[#374151]">{city.state}</p>
                </div>
                <i className="ri-arrow-right-line text-2xl text-[#E8B84B] group-hover:translate-x-1 transition-transform duration-300"></i>
              </div>
              
              <p className="text-sm text-[#374151] mb-3">
                {city.distance}
              </p>
              
              <div className="flex items-center gap-2 text-sm text-[#1C2B1E]">
                <i className="ri-map-pin-line text-[#E8B84B]"></i>
                <span>View service details</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}